import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, Menu, X, Phone, ChevronDown, Truck, ShieldCheck, Zap } from 'lucide-react';
import { LOGO } from '../data/assets';
import { useCart } from '../context/CartContext';
import { getCategories, getSettings } from '../lib/api';

const MARQUEE = [
  { icon: Truck, text: 'Free Nationwide Delivery Across Pakistan' },
  { icon: ShieldCheck, text: '14-Month Warranty on All Products' },
  { icon: Zap, text: 'Save Up To 86% Energy with National Light LED' },
  { icon: Phone, text: 'Order Now on WhatsApp — We Bright The Nation' },
];

const Header = () => {
  const { count } = useCart();
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [settings, setSettings] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState('');
  const [showCats, setShowCats] = useState(false);

  useEffect(() => {
    getCategories().then(setCats).catch(() => {});
    getSettings().then(setSettings).catch(() => {});
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/shop?search=${encodeURIComponent(q)}`);
    setMobileOpen(false);
  };

  const marqueeItems = [...MARQUEE, ...MARQUEE];

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm">
      {/* marquee top bar */}
      <div className="hero-gradient text-white overflow-hidden">
        <div className="max-w-[1600px] mx-auto relative">
          <div className="marquee-track py-2">
            {marqueeItems.map((m, i) => (
              <span key={i} className="flex items-center gap-2 text-xs md:text-sm font-medium">
                <m.icon size={15} className="text-[#8bc34a]" /> {m.text}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* main */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
        <Link to="/" className="shrink-0">
          <img src={LOGO} alt="National Light" className="h-12 md:h-14 w-auto" />
        </Link>

        <form onSubmit={doSearch} className="hidden md:flex flex-1 max-w-xl mx-auto">
          <div className="flex w-full rounded-full overflow-hidden border-2 border-[#0f7a37] focus-within:shadow-md transition">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search LED bulbs, panels, flood lights..."
              className="flex-1 px-5 py-2.5 outline-none text-sm" />
            <button type="submit" className="bg-[#0f7a37] hover:bg-[#0d5228] text-white px-5 flex items-center transition-colors">
              <Search size={18} />
            </button>
          </div>
        </form>

        <div className="flex items-center gap-3 ml-auto md:ml-0">
          <a href={`tel:${settings.phone || ''}`} className="hidden lg:flex items-center gap-2 text-sm">
            <span className="w-9 h-9 rounded-full bg-[#eaf6ec] flex items-center justify-center"><Phone size={16} className="text-[#0f7a37]" /></span>
            <span className="leading-tight"><span className="block text-gray-400 text-xs">Call us</span><span className="font-semibold text-[#0d5228]">{settings.phone || '+92 300 1234567'}</span></span>
          </a>
          <Link to="/cart" className="relative p-2 rounded-full hover:bg-[#eaf6ec] transition-colors">
            <ShoppingCart size={24} className="text-[#0d5228]" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#f5a623] text-white text-[11px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{count}</span>
            )}
          </Link>
          <button className="md:hidden p-2" onClick={() => setMobileOpen(v => !v)}>
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* nav */}
      <nav className="hidden md:block border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 flex items-center gap-1">
          <Link to="/" className="px-4 py-3 text-sm font-semibold text-[#14181b] hover:text-[#0f7a37] transition-colors">Home</Link>
          <div className="relative" onMouseEnter={() => setShowCats(true)} onMouseLeave={() => setShowCats(false)}>
            <Link to="/shop" className="px-4 py-3 text-sm font-semibold text-[#14181b] hover:text-[#0f7a37] transition-colors flex items-center gap-1">
              Shop <ChevronDown size={15} />
            </Link>
            {showCats && (
              <div className="absolute left-0 top-full bg-white shadow-xl rounded-lg border border-gray-100 w-64 py-2 z-50">
                {cats.map(c => (
                  <Link key={c.slug} to={`/shop?category=${encodeURIComponent(c.name)}`}
                    className="flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-[#eaf6ec] hover:text-[#0f7a37] transition-colors">
                    {c.name} <span className="text-xs text-gray-400">{c.count}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link to="/shop" className="px-4 py-3 text-sm font-semibold text-[#14181b] hover:text-[#0f7a37] transition-colors">Products</Link>
          <Link to="/about" className="px-4 py-3 text-sm font-semibold text-[#14181b] hover:text-[#0f7a37] transition-colors">About Us</Link>
          <Link to="/manufacturing" className="px-4 py-3 text-sm font-semibold text-[#14181b] hover:text-[#0f7a37] transition-colors">Manufacturing</Link>
          <Link to="/contact" className="px-4 py-3 text-sm font-semibold text-[#14181b] hover:text-[#0f7a37] transition-colors">Contact Us</Link>
        </div>
      </nav>

      {/* mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white px-4 py-3 max-h-[70vh] overflow-y-auto">
          <form onSubmit={doSearch} className="flex mb-3">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search products..."
              className="flex-1 px-4 py-2 border-2 border-[#0f7a37] rounded-l-full outline-none text-sm" />
            <button className="bg-[#0f7a37] text-white px-4 rounded-r-full"><Search size={18} /></button>
          </form>
          <Link to="/" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">Home</Link>
          <Link to="/shop" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">Shop All Products</Link>
          {cats.map(c => (
            <Link key={c.slug} to={`/shop?category=${encodeURIComponent(c.name)}`} onClick={() => setMobileOpen(false)}
              className="block py-2 pl-3 text-sm text-gray-600">{c.name} ({c.count})</Link>
          ))}
          <Link to="/about" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">About Us</Link>
          <Link to="/manufacturing" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">Manufacturing</Link>
          <Link to="/contact" onClick={() => setMobileOpen(false)} className="block py-2 font-semibold">Contact Us</Link>
        </div>
      )}
    </header>
  );
};

export default Header;
